import followAPI from './follow.api';
import userStatsService from './userStats.service';

class FollowService {
  constructor() {
    this.followStatusCache = new Map();
    this.followersCache = new Map();
    this.followingCache = new Map();
    this.suggestionsCache = null;
    this.cacheDuration = 10 * 60 * 1000; // 10 minutes
  }

  // Check if user is following another user
  async checkFollowStatus(userId, targetUserId) {
    const cacheKey = `${userId}_${targetUserId}`;
    const cached = this.followStatusCache.get(cacheKey);

    if (cached && (Date.now() - cached.timestamp) < this.cacheDuration) {
      return cached.data;
    }

    try {
      const response = await followAPI.checkFollowStatus(targetUserId);
      const data = response.data;
      
      this.followStatusCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error('Check follow status error:', error);
      // Return cached data if available
      if (cached) return cached.data;
      return { following: false };
    }
  }

  // Follow/Unfollow user
  async toggleFollow(targetUserId) {
    try {
      const response = await followAPI.toggleFollow(targetUserId);
      const { following, stats } = response.data;

      // Update cache
      const cacheKey = `user_${targetUserId}`;
      this.followStatusCache.set(cacheKey, {
        data: { following },
        timestamp: Date.now()
      });

      // Update user stats
      if (stats) {
        userStatsService.updateStats('me', {
          followingCount: stats.followingCount
        });
        
        // Update the target user's follower count in their profile if cached
        const targetUserStats = userStatsService.getUserStats(targetUserId);
        if (targetUserStats) {
          userStatsService.updateStats(targetUserId, {
            followersCount: following 
              ? (targetUserStats.followersCount || 0) + 1 
              : Math.max(0, (targetUserStats.followersCount || 1) - 1)
          });
        }
      }

      // Clear related caches
      this.clearUserCache(targetUserId);

      return response.data;
    } catch (error) {
      console.error('Toggle follow error:', error);
      throw error;
    }
  }

  // Get user's followers
  async getFollowers(userId, forceRefresh = false) {
    const cacheKey = `followers_${userId}`;
    const cached = this.followersCache.get(cacheKey);

    if (!forceRefresh && cached && (Date.now() - cached.timestamp) < this.cacheDuration) {
      return cached.data;
    }

    try {
      const response = await followAPI.getFollowers(userId, { limit: 50 });
      const data = response.data;
      
      this.followersCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error('Get followers error:', error);
      if (cached) return cached.data;
      return { followers: [], pagination: { total: 0 } };
    }
  }

  // Get user's following
  async getFollowing(userId, forceRefresh = false) {
    const cacheKey = `following_${userId}`;
    const cached = this.followingCache.get(cacheKey);

    if (!forceRefresh && cached && (Date.now() - cached.timestamp) < this.cacheDuration) {
      return cached.data;
    }

    try {
      const response = await followAPI.getFollowing(userId, { limit: 50 });
      const data = response.data;
      
      this.followingCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (error) {
      console.error('Get following error:', error);
      if (cached) return cached.data;
      return { following: [], pagination: { total: 0 } };
    }
  }

  // Get follow suggestions
  async getFollowSuggestions(forceRefresh = false) {
    if (!forceRefresh && this.suggestionsCache && (Date.now() - this.suggestionsCache.timestamp) < this.cacheDuration) {
      return this.suggestionsCache.data;
    }

    try {
      const response = await followAPI.getFollowSuggestions({ limit: 10 });
      const data = response.data;
      
      this.suggestionsCache = {
        data,
        timestamp: Date.now()
      };

      return data;
    } catch (error) {
      console.error('Get follow suggestions error:', error);
      if (this.suggestionsCache) return this.suggestionsCache.data;
      return { suggestions: [] };
    }
  }

  // Get mutual follows
  async getMutualFollows(userId) {
    try {
      const response = await followAPI.getMutualFollows(userId);
      return response.data;
    } catch (error) {
      console.error('Get mutual follows error:', error);
      return { mutualFollows: [] };
    }
  }

  // Batch check follow status for multiple users
  async batchCheckFollowStatus(userIds) {
    try {
      const results = {};
      const uncachedUsers = [];
      
      // Check cache first
      userIds.forEach(userId => {
        const cacheKey = `user_${userId}`;
        const cached = this.followStatusCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) < this.cacheDuration) {
          results[userId] = cached.data;
        } else {
          uncachedUsers.push(userId);
        }
      });

      // Fetch uncached users
      if (uncachedUsers.length > 0) {
        // Note: You'd need a batch endpoint in backend for this
        // For now, fetch individually
        const promises = uncachedUsers.map(userId => 
          this.checkFollowStatus('current', userId)
        );
        const fetchedResults = await Promise.all(promises);
        
        uncachedUsers.forEach((userId, index) => {
          results[userId] = fetchedResults[index];
        });
      }

      return results;
    } catch (error) {
      console.error('Batch check follow status error:', error);
      return {};
    }
  }

  // Clear cache for specific user
  clearUserCache(userId) {
    this.followStatusCache.delete(`user_${userId}`);
    this.followersCache.delete(`followers_${userId}`);
    this.followingCache.delete(`following_${userId}`);
  }

  // Clear all cache
  clearCache() {
    this.followStatusCache.clear();
    this.followersCache.clear();
    this.followingCache.clear();
    this.suggestionsCache = null;
  }

  // Helper: Format follow stats for display
  formatFollowStats(stats) {
    if (!stats) return { followers: 0, following: 0 };
    
    return {
      followers: stats.followersCount || 0,
      following: stats.followingCount || 0,
    };
  }
}

export default new FollowService();